// useInputFocusHandler.ts
import { useRef, useCallback } from 'preact/hooks';
import type { Dispatch } from 'preact/hooks';

type SelectFirstAction = { actionType: 'selectFirst' };

/**
 * Hook that determines whether to dispatch a 'selectFirst' action when an input field receives focus.
 *
 * @param {Object} options
 * @param {Dispatch<A>} options.dispatch - Dispatch function for actions of type A that must contain {actionType: 'selectFirst'}.
 * @param {boolean} [options.isInputFocused] - Whether the input field is currently focused.
 * @returns {function} - An `onPointerDown` handler.
 */
const useInputMaskSelectFirstHandler = <A extends SelectFirstAction>({
  dispatch,
  isInputFocused
}: {
  dispatch: Dispatch<A>;
  isInputFocused?: boolean;
}) => {
  // The following is a simplified version of how we detect keyboard vs. mouse focus
  // in the useCollectionFocusRing hook. If the user tabs onto the field, we want to
  // automatically select the first segment.
  const recentPointerRef = useRef<boolean>(false);
  const pointerDownTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const onPointerDown = useCallback(() => {
    recentPointerRef.current = true;
    clearTimeout(pointerDownTimerRef.current);

    pointerDownTimerRef.current = setTimeout(() => {
      recentPointerRef.current = false;
    }, 200);
  }, []);

  const lastInputFocusedRef = useRef<boolean>(false);
  // If we receive focus that is *not* due to the user clicking in the field, i.e. the user
  // tabbed onto the field, then select the first segment. (!recentPointerRef.current means the mouse wasn't used in the component).
  // Note: we cannot check isFocused because it includes the ? icon in compact UAD, and we don't
  // want the text selected when the help icon is focused.
  // Every call to the reducer is always producing a new object and then when we create a new object, this hook gets called again. There are libraries that would fix this.
  if (isInputFocused && !lastInputFocusedRef.current && !recentPointerRef.current) {
    const action: SelectFirstAction = { actionType: 'selectFirst' };
    dispatch(action as A); // safe because A must include SelectFirstAction
  }

  // If the inputFocus has changed, then set recentPointerRef.current to false to not wait until the timeout, and update the lastInputFocusedRef.
  // Setting refs does not cause a re-render.
  if (isInputFocused !== lastInputFocusedRef.current) {
    recentPointerRef.current = false;
    lastInputFocusedRef.current = !!isInputFocused;
  }

  return onPointerDown;
};

export { useInputMaskSelectFirstHandler };
